import { ArrowDown, ArrowUp, Eye, EyeOff, ExternalLink, KanbanSquare, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { CallOutcomesSection } from "@/components/CallOutcomesSection";
import { FieldProfilesSection } from "@/components/FieldProfilesSection";
import { GuidedScriptsSection } from "@/components/GuidedScriptsSection";
import { TestLeadSection } from "@/components/TestLeadSection";
import { WonStepsSection } from "@/components/WonStepsSection";
import { SiteRoutingSection } from "@/components/SiteRoutingSection";

const errText = (e) => e?.response?.data?.detail || "Erreur, réessayez.";

export default function PipelineSettingsPage() {
  const [data, setData] = useState(null);
  const [openName, setOpenName] = useState(null);
  const [saving, setSaving] = useState(false);
  const [denied, setDenied] = useState(false);
  useEffect(() => { api.get("/settings/pipeline").then(({ data: d }) => setData(d)).catch((e) => (e?.response?.status === 403 ? setDenied(true) : toast.error(errText(e)))); }, []);

  const persist = async (pipelines) => {
    setData((d) => ({ ...d, pipelines }));
    setSaving(true);
    try {
      const body = { order: pipelines.map((p) => p.name), hidden_pipelines: pipelines.filter((p) => p.hidden).map((p) => p.name), hidden_steps: pipelines.flatMap((p) => p.steps.filter((s) => s.hidden).map((s) => s.id)) };
      setData((await api.put("/settings/pipeline", body)).data);
    } catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };
  const move = (i, dir) => { const list = [...data.pipelines]; const j = i + dir; if (j < 0 || j >= list.length) return; [list[i], list[j]] = [list[j], list[i]]; persist(list); };
  const togglePipeline = (i) => {
    const p = data.pipelines[i];
    if (!p.hidden && !window.confirm(`Archiver « ${p.name} » ? Ses fiches restent consultables (filtre « Afficher les pipelines archivés ») mais sortent des listes, compteurs, Cockpit, statistiques et automatismes. Rien n'est modifié dans noCRM.`)) return;
    toast.success(p.hidden ? `« ${p.name} » réactivé.` : `« ${p.name} » archivé.`);
    persist(data.pipelines.map((p, k) => (k === i ? { ...p, hidden: !p.hidden } : p)));
  };
  const toggleStep = (i, id) => persist(data.pipelines.map((p, k) => (k === i ? { ...p, steps: p.steps.map((s) => (s.id === id ? { ...s, hidden: !s.hidden } : s)) } : p)));
  const reload = async () => { try { setData((await api.post("/settings/pipeline/refresh")).data); toast.success("Pipelines et étapes relus depuis noCRM."); } catch (e) { toast.error(errText(e)); } };

  if (denied) return <section className="page"><p className="pl-warning" data-testid="admin-only">Réservé aux administrateurs.</p></section>;
  if (!data) return <section className="page"><p className="pl-muted">Chargement…</p></section>;
  const admin = data.subdomain ? `https://${data.subdomain}.nocrm.io/admin` : null;
  return (
    <section className="page" data-testid="pipeline-settings-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">PARAMÈTRES</p>
          <h1>Pipelines</h1>
          <p className="page-subtitle">Ordre d'affichage et pipelines ou étapes masqués dans la rubrique Pipeline. Par défaut, l'ordre est celui de noCRM.</p>
        </div>
        <div className="button-row">
          <button type="button" className="secondary-button" onClick={reload} data-testid="pipeline-settings-refresh"><RefreshCw size={15} /> Relire depuis noCRM</button>
          {admin && <a className="primary-button" href={admin} target="_blank" rel="noreferrer" data-testid="pipeline-settings-nocrm-admin"><ExternalLink size={15} /> Créer un pipeline ou une étape dans noCRM</a>}
        </div>
      </header>
      <SiteRoutingSection />
      <FieldProfilesSection />
      <CallOutcomesSection pipelines={data.pipelines} />
      <WonStepsSection pipelines={data.pipelines} />
      <GuidedScriptsSection />
      <TestLeadSection />
      <section className="settings-section">
        <div className="section-title"><KanbanSquare size={20} /><div><p className="eyebrow">{data.pipelines.length} PIPELINES{saving ? " · ENREGISTREMENT…" : ""}</p><h2>Ordre et visibilité</h2></div></div>
        <p className="fileprep-dedup-hint">noCRM ne permet pas de créer des pipelines ni des étapes depuis un autre outil : créez-les dans noCRM (bouton ci-dessus), puis cliquez sur « Relire depuis noCRM ».</p>
        <ol className="pls-list">
          {data.pipelines.map((p, i) => (
            <li key={p.name} className={p.hidden ? "is-hidden" : ""} data-testid={`pipeline-settings-row-${i}`}>
              <div className="pls-row">
                <span className="pls-rank">{i + 1}</span>
                <button type="button" className="pls-name" onClick={() => setOpenName(openName === p.name ? null : p.name)} data-testid={`pipeline-settings-open-${i}`}>{p.name} <small>{p.steps.length} étapes</small></button>
                <button type="button" className="pl-icon-btn" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Monter" data-testid={`pipeline-settings-up-${i}`}><ArrowUp size={14} /></button>
                <button type="button" className="pl-icon-btn" disabled={i === data.pipelines.length - 1} onClick={() => move(i, 1)} aria-label="Descendre" data-testid={`pipeline-settings-down-${i}`}><ArrowDown size={14} /></button>
                <button type="button" className="secondary-button" onClick={() => togglePipeline(i)} data-testid={`pipeline-settings-toggle-${i}`}>{p.hidden ? <><EyeOff size={14} /> Archivé · réactiver</> : <><Eye size={14} /> Actif</>}</button>
              </div>
              {openName === p.name && (
                <div className="pls-steps">
                  {p.steps.map((s) => (
                    <button key={s.id} type="button" className={`rc-chip${s.hidden ? "" : " on"}`} onClick={() => toggleStep(i, s.id)} data-testid={`pipeline-settings-step-${s.id}`}>{s.hidden ? <EyeOff size={12} /> : <Eye size={12} />} {s.name}</button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      </section>
    </section>
  );
}
